import React, {useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef} from 'react';
import {Box, Typography, Button} from '@material-ui/core';

const HZ_COUNT = 30

/**
 * 0~30Hz 주파수 이퀄라이저
 * 각 Hz별 계수를 드래그로 조절 (0.1 단위)
 * onChange(curve) 콜백으로 30개 배열 전달
 */
const FrequencyEqualizer = forwardRef(({onChange, width = 600, height = 150, savedCurve, defaultValue = 0, label = '주파수 이퀄라이저 (0~30Hz)', min = -5, max = 5}, ref) => {
    const defaultCurve = new Array(HZ_COUNT).fill(defaultValue)
    const canvasRef = useRef(null)
    const [curve, setCurve] = useState(savedCurve && savedCurve.length === HZ_COUNT ? [...savedCurve] : [...defaultCurve])
    const curveRef = useRef(curve)
    curveRef.current = curve
    const isDragging = useRef(false)

    useImperativeHandle(ref, () => ({
        getCurve: () => curveRef.current,
        reset: () => {
            const newCurve = [...defaultCurve]
            setCurve(newCurve)
            curveRef.current = newCurve
            if (onChange) onChange(newCurve)
        }
    }))

    const barWidth = width / HZ_COUNT
    const range = max - min

    // 값 → Y 좌표 변환 (max가 top, min이 bottom)
    const valToY = (val, h) => h - ((val - min) / range) * h
    // Y 좌표 → 값 변환
    const yToVal = (y, h) => (1 - y / h) * range + min

    const draw = useCallback(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        const w = canvas.width
        const h = canvas.height

        ctx.clearRect(0, 0, w, h)

        // 배경
        ctx.fillStyle = 'rgba(0,0,0,0.85)'
        ctx.fillRect(0, 0, w, h)

        // 1 단위 가이드라인
        for (let v = min; v <= max; v = Math.round((v + 1) * 10) / 10) {
            const lineY = valToY(v, h)
            if (v === defaultValue) {
                ctx.strokeStyle = 'rgba(255,255,255,0.5)'
                ctx.setLineDash([])
            } else if (v % 5 === 0) {
                ctx.strokeStyle = 'rgba(255,255,255,0.3)'
                ctx.setLineDash([4, 4])
            } else {
                ctx.strokeStyle = 'rgba(255,255,255,0.1)'
                ctx.setLineDash([2, 4])
            }
            ctx.beginPath()
            ctx.moveTo(0, lineY)
            ctx.lineTo(w, lineY)
            ctx.stroke()
        }
        ctx.setLineDash([])

        // 바 그리기 (defaultValue 기준으로 위/아래)
        const data = curveRef.current
        const baseY = valToY(defaultValue, h)
        for (let i = 0; i < HZ_COUNT; i++) {
            const x = i * barWidth
            const valY = valToY(data[i], h)

            // 바 색상: 기본값 이상=주황~빨강, 이하=파랑~남색
            if (data[i] > defaultValue) {
                const t = (data[i] - defaultValue) / (max - defaultValue || 1)
                ctx.fillStyle = `rgba(${Math.round(200 + 55 * t)}, ${Math.round(180 * (1 - t))}, 50, 0.8)`
            } else {
                const t = (defaultValue - data[i]) / (defaultValue - min || 1)
                ctx.fillStyle = `rgba(50, ${Math.round(150 * (1 - t))}, ${Math.round(200 + 55 * t)}, 0.8)`
            }

            if (data[i] >= defaultValue) {
                ctx.fillRect(x + 1, valY, barWidth - 2, baseY - valY)
            } else {
                ctx.fillRect(x + 1, baseY, barWidth - 2, valY - baseY)
            }
        }

        // 곡선
        ctx.strokeStyle = '#FFC004'
        ctx.lineWidth = 2
        ctx.beginPath()
        for (let i = 0; i < HZ_COUNT; i++) {
            const x = i * barWidth + barWidth / 2
            const y = valToY(data[i], h)
            if (i === 0) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
        }
        ctx.stroke()

        // 포인트
        for (let i = 0; i < HZ_COUNT; i++) {
            const x = i * barWidth + barWidth / 2
            const y = valToY(data[i], h)
            ctx.fillStyle = '#FFC004'
            ctx.beginPath()
            ctx.arc(x, y, 3, 0, Math.PI * 2)
            ctx.fill()
        }

        // Hz 라벨 (5Hz 간격)
        ctx.fillStyle = 'rgba(255,255,255,0.6)'
        ctx.font = '10px sans-serif'
        ctx.textAlign = 'center'
        for (let i = 0; i < HZ_COUNT; i += 5) {
            const x = i * barWidth + barWidth / 2
            ctx.fillText(`${i}`, x, h - 2)
        }
        ctx.fillText('30', (HZ_COUNT - 1) * barWidth + barWidth / 2, h - 2)

        // 좌측 값 라벨
        ctx.fillStyle = 'rgba(255,255,255,0.5)'
        ctx.textAlign = 'left'
        ctx.font = '9px sans-serif'
        ctx.fillText(max > 0 ? `+${max}` : `${max}`, 2, 10)
        ctx.fillText(`${defaultValue}`, 2, baseY - 2)
        ctx.fillText(`${min}`, 2, h - 12)
    }, [barWidth, height, width, min, max, defaultValue, range])

    useEffect(() => {
        draw()
    }, [curve, draw])

    const getHzFromEvent = (e) => {
        const canvas = canvasRef.current
        const rect = canvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const hz = Math.floor(x / barWidth)
        const raw = yToVal(y, canvas.height)
        const clamped = Math.max(min, Math.min(max, raw))
        const val = Math.round(clamped * 10) / 10  // 0.1 단위 스냅
        return {hz: Math.max(0, Math.min(HZ_COUNT - 1, hz)), val}
    }

    const applyChange = (hz, val) => {
        const newCurve = [...curveRef.current]
        newCurve[hz] = val
        setCurve(newCurve)
        curveRef.current = newCurve
        if (onChange) onChange(newCurve)
    }

    const handleMouseDown = (e) => {
        isDragging.current = true
        const {hz, val} = getHzFromEvent(e)
        applyChange(hz, val)
    }

    const handleMouseMove = (e) => {
        if (!isDragging.current) return
        const {hz, val} = getHzFromEvent(e)
        applyChange(hz, val)
    }

    const handleMouseUp = () => {
        isDragging.current = false
    }

    return (
        <Box style={{position: 'relative'}}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{marginBottom: 4}}>
                <Typography style={{color: '#fff', fontSize: 12, fontWeight: 'bold'}}>
                    {label}
                </Typography>
                <Box display="flex" alignItems="center" style={{gap: 4}}>
                    <Button
                        size="small"
                        style={{color: '#fff', fontSize: 11, minWidth: 'auto', padding: '0 8px'}}
                        onClick={() => {
                            const newCurve = curveRef.current.map(v => Math.min(max, Math.round((v + 0.1) * 10) / 10))
                            setCurve(newCurve)
                            curveRef.current = newCurve
                            if (onChange) onChange(newCurve)
                        }}
                    >
                        전체 +0.1
                    </Button>
                    <Button
                        size="small"
                        style={{color: '#fff', fontSize: 11, minWidth: 'auto', padding: '0 8px'}}
                        onClick={() => {
                            const newCurve = curveRef.current.map(v => Math.max(min, Math.round((v - 0.1) * 10) / 10))
                            setCurve(newCurve)
                            curveRef.current = newCurve
                            if (onChange) onChange(newCurve)
                        }}
                    >
                        전체 -0.1
                    </Button>
                    <Button
                        size="small"
                        style={{color: '#fff', fontSize: 11, minWidth: 'auto', padding: '0 8px'}}
                        onClick={() => {
                            const newCurve = [...defaultCurve]
                            setCurve(newCurve)
                            curveRef.current = newCurve
                            if (onChange) onChange(newCurve)
                        }}
                    >
                        초기화
                    </Button>
                </Box>
            </Box>
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{borderRadius: 4, cursor: 'crosshair', display: 'block'}}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            />
        </Box>
    )
})

export default FrequencyEqualizer
